import { useRenameFileRecord } from "@/api/fileRecords";
import { ZenseiApiErrorBody } from "@/api/utils";
import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import { isAxiosError } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

interface EditableFileRecordNameProps {
  record: FileRecord;
  /** When false, the name is rendered as a static label (viewers can't rename). */
  canEdit?: boolean;
}

/**
 * Click-to-edit filename in the file-record detail pane header.
 *
 * Mirrors `CanvasTopBarV2`'s rename pattern (single-click → input,
 * Enter / blur to save, Escape to revert, mirror-span auto-grow) but
 * adapted for files: the extension is preserved server-side, and we
 * reveal it as a muted, non-editable suffix only while editing so the
 * user understands that part is locked. In read mode the header stays
 * clean (just the stem) per product direction.
 *
 * Edits map to `POST /file-records/:id/move` with `destination` set to
 * the source's own scope (same class + workspace/canvas/flow_session
 * id), changing only the leaf segment of `path`. See
 * {@link useRenameFileRecord} for the cache-update strategy
 * (optimistic per-id update + server-response refresh to repair the
 * presigned URL after the S3 key changes).
 *
 * Editing is gated on `status === "processed"` because the backend's
 * move primitive rejects non-processed records (returns 400). Showing
 * the editable affordance pre-`processed` would dead-end the user, so
 * pre-`processed` we render the name as a static, non-clickable label.
 */
export function EditableFileRecordName({
  record,
  canEdit = true,
}: EditableFileRecordNameProps) {
  const canRename = record.status === "processed" && canEdit;
  const fullName = getFileName(record.path);
  const { stem: storedStem, ext } = splitFileName(fullName);

  const [isEditing, setIsEditing] = useState(false);
  const [draftStem, setDraftStem] = useState(storedStem);
  const inputRef = useRef<HTMLInputElement>(null);

  // Match `CanvasTopBarV2`: when an external update lands (e.g. WS event
  // for a concurrent rename) while the user is mid-edit, leave the draft
  // untouched. Otherwise typing would get clobbered every time the cache
  // patches in.
  const isEditingRef = useRef(false);

  useEffect(() => {
    if (!isEditingRef.current) {
      setDraftStem(storedStem);
    }
  }, [storedStem]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // Pre-select the stem so the user can type a replacement
      // immediately. Mirrors the workspace rename dialog's UX.
      inputRef.current.setSelectionRange(0, draftStem.length);
    }
    // Only re-run on edit-mode toggle — selection on every keystroke
    // would fight the user's typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const renameFileRecord = useRenameFileRecord();

  const enterEdit = useCallback(() => {
    if (!canRename) return;
    isEditingRef.current = true;
    setIsEditing(true);
  }, [canRename]);

  const exitEdit = useCallback(() => {
    isEditingRef.current = false;
    setIsEditing(false);
  }, []);

  const handleCancel = useCallback(() => {
    setDraftStem(storedStem);
    exitEdit();
  }, [storedStem, exitEdit]);

  const handleSave = useCallback(() => {
    // Use the ref, not the state. `setIsEditing(false)` from `exitEdit`
    // is batched, so the closure captured for `onBlur` still sees
    // `isEditing === true` after Escape or after a successful Enter.
    // When React unmounts the input, the focused-element removal fires
    // a delegated `focusout` → synthetic `onBlur`, replaying this
    // callback with the stale closure. Without the ref guard:
    //   - Escape → `handleCancel` → unmount → `onBlur` saves the
    //     cancelled edit anyway.
    //   - Enter  → first save fires + unmount → `onBlur` fires a
    //     duplicate mutation with the same payload.
    // `isEditingRef.current` flips synchronously inside `exitEdit`, so
    // both replay paths short-circuit here.
    if (!isEditingRef.current) return;

    const trimmed = draftStem.trim();

    // Empty or unchanged → revert silently.
    if (trimmed.length === 0 || trimmed === storedStem) {
      handleCancel();
      return;
    }

    // Reject path separators client-side. Backend rejects these too
    // but the toast UX is better when we never even fire the request.
    if (trimmed.includes("/") || trimmed.includes("\\")) {
      toast.error("Name cannot contain '/' or '\\'");
      return;
    }

    const parent = getParentPath(record.path);
    const newPath = `${parent}${trimmed}${ext}`;

    exitEdit();
    renameFileRecord.mutate(
      { record, newPath },
      {
        onError: (err) => {
          // Roll the input back to the stored name on failure (the hook
          // already rolled the cache back; we just need to re-sync local
          // state).
          setDraftStem(storedStem);
          if (isAxiosError(err) && err.response?.status === 409) {
            toast.error(`A file named "${trimmed}${ext}" already exists.`);
          } else if (isAxiosError(err) && err.response?.status === 403) {
            toast.error("You don't have permission to rename this file.");
          } else if (
            isAxiosError<ZenseiApiErrorBody>(err) &&
            err.response?.status === 422
          ) {
            // Backend returns 422 for state-based denials (e.g. source file
            // is not in a renamable status). Surface the server's message
            // since it's more specific than anything we'd hard-code here.
            const serverMessage = err.response?.data?.error?.message;
            toast.error(serverMessage ?? "Failed to rename file.");
          } else {
            toast.error("Failed to rename file.");
          }
        },
      },
    );
  }, [
    draftStem,
    storedStem,
    ext,
    record,
    renameFileRecord,
    exitEdit,
    handleCancel,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSave();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleCancel();
      }
    },
    [handleSave, handleCancel],
  );

  if (!canRename) {
    return (
      <p
        className="truncate text-[13px] font-medium text-foreground"
        title={fullName}
      >
        {fullName}
      </p>
    );
  }

  if (!isEditing) {
    return (
      <button
        type="button"
        onClick={enterEdit}
        className={cn(
          "min-w-0 max-w-full truncate rounded-md px-1.5 py-0.5 -mx-1.5",
          "text-[13px] font-medium text-foreground text-left",
          "hover:bg-foreground/5 dark:hover:bg-n-95/5 transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
        title={fullName}
      >
        {storedStem}
      </button>
    );
  }

  return (
    <div className="flex min-w-0 items-center text-[13px] font-medium">
      {/* Auto-grow via a hidden mirror span (matches CanvasTopBarV2) so the
         input width tracks the typed text. We cap with `max-w-[60ch]` so a
         very long name can't push the header actions off-screen. */}
      <div
        className="relative grid max-w-[60ch] min-w-[6ch] shrink"
        style={{ minWidth: "6ch" }}
      >
        <span
          aria-hidden
          className="invisible whitespace-pre px-1.5 py-0.5 col-start-1 row-start-1"
        >
          {draftStem || "Untitled"}
        </span>
        <input
          ref={inputRef}
          type="text"
          value={draftStem}
          onChange={(e) => setDraftStem(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          className={cn(
            "absolute inset-0 px-1.5 py-0.5 w-full",
            "bg-foreground/5 dark:bg-n-95/5 rounded-md",
            "border-0 outline-none ring-1 ring-ring/40",
            "text-foreground",
          )}
          placeholder="Untitled"
          aria-label="File name"
        />
      </div>
      {ext && (
        <span
          aria-hidden
          className="px-0.5 py-0.5 text-muted-foreground select-none"
        >
          {ext}
        </span>
      )}
    </div>
  );
}

/** Last segment of a (file) path. Folders aren't expected in the detail pane. */
function getFileName(path: string): string {
  const segments = path.split("/").filter(Boolean);
  return segments[segments.length - 1] ?? path;
}

/** Everything up to (and including) the last `/`, or `""` for top-level files. */
function getParentPath(path: string): string {
  const lastSlash = path.lastIndexOf("/");
  if (lastSlash === -1) return "";
  return path.slice(0, lastSlash + 1);
}

/**
 * Split a file name into the editable stem and the locked extension.
 * Mirrors the workspace rename dialog: only the *last* `.ext` is locked,
 * dotfiles (`.env`) have no extension, files with no dot are fully
 * editable. Trailing dots ("foo.") are treated as no-extension since
 * they'd otherwise lock an empty suffix.
 */
function splitFileName(name: string): { stem: string; ext: string } {
  const lastDot = name.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === name.length - 1) {
    return { stem: name, ext: "" };
  }
  return { stem: name.slice(0, lastDot), ext: name.slice(lastDot) };
}
